import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSiteContent } from "@/context/SiteContentContext";
import { LogIn, AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

const DEFAULT_HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/bea3c376-67c0-4264-85e7-8b27d546bc57/images/c19ead953dcd679087fbb2cf927bc9b2259e1471c6783da5a8b2fae941f235ca.png";

function LoginForm({ onSubmit, email, setEmail, password, setPassword, err, loading, content, style }) {
    const { t } = useTranslation();
    const base = (
        <>
            <div className="label-kicker mb-2" style={{ color: content.login_form_text_color || undefined }}>{t("auth.login_kicker")}</div>
            <h1
                className="font-display font-black text-3xl uppercase mb-6"
                style={{ color: content.login_form_text_color || undefined }}
                data-testid="login-title"
            >
                {content.login_title}
            </h1>

            <form onSubmit={onSubmit} className="space-y-4">
                <div>
                    <label className="label-kicker mb-1 block" style={{ color: content.login_form_text_color || undefined }}>{t("auth.email")}</label>
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="input-field"
                        data-testid="login-email-input"
                    />
                </div>
                <div>
                    <label className="label-kicker mb-1 block" style={{ color: content.login_form_text_color || undefined }}>{t("auth.password")}</label>
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="input-field"
                        data-testid="login-password-input"
                    />
                </div>
                {err && (
                    <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2" data-testid="login-error">
                        <AlertCircle className="w-4 h-4" />
                        <span>{err}</span>
                    </div>
                )}
                <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="login-submit">
                    <LogIn className="w-4 h-4" /> {loading ? t("auth.signing_in") : t("auth.sign_in")}
                </button>
            </form>

            <p className="mt-6 text-sm" style={{ color: content.login_form_text_color ? content.login_form_text_color + "cc" : undefined }}>
                {t("auth.no_account")}{" "}
                <Link to="/register" className="text-[hsl(var(--primary))] font-semibold">
                    {t("auth.request_access")}
                </Link>
            </p>
        </>
    );

    if (style === "glass") {
        return (
            <div className="w-full max-w-sm backdrop-blur-xl bg-white/20 border border-white/30 shadow-2xl p-8" data-testid="login-form-card">
                {base}
            </div>
        );
    }
    if (style === "minimal") {
        return (
            <div className="w-full max-w-sm" data-testid="login-form-card">
                {base}
            </div>
        );
    }
    // card (default)
    return (
        <div className="w-full max-w-sm bg-white border border-slate-200 shadow-xl p-8" data-testid="login-form-card">
            {base}
        </div>
    );
}

function LogoBlock({ content, size }) {
    if (content.logo_url) {
        return (
            <img
                src={content.logo_url}
                alt={content.brand_name}
                style={{ width: `${size}px`, height: `${size}px` }}
                className="object-contain"
                data-testid="login-logo"
            />
        );
    }
    return (
        <div
            className="bg-[hsl(var(--primary))] flex items-center justify-center text-white font-display font-black"
            style={{
                width: `${size}px`,
                height: `${size}px`,
                fontSize: `${Math.max(12, Math.round(size / 2))}px`,
            }}
            data-testid="login-logo"
        >
            {content.brand_short}
        </div>
    );
}

export default function LoginPage() {
    const { login } = useAuth();
    const { content } = useSiteContent();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        const res = await login(email, password);
        setLoading(false);
        if (res.ok) {
            if (res.user?.role === "admin") nav("/admin");
            else if (res.user?.status === "approved") nav("/");
            else nav("/pending");
        } else {
            setErr(res.error);
        }
    };

    const bgUrl = content.login_background_url || DEFAULT_HERO_IMG;
    const bgOpacity = content.login_background_opacity ?? 0.4;
    const logoSize = content.login_logo_size || 36;
    const line2Color = content.login_hero_line2_color || "hsl(var(--primary))";
    const layout = content.login_layout || "centered";
    const formStyle = content.login_form_style || "card";

    const formProps = { onSubmit, email, setEmail, password, setPassword, err, loading, content, style: formStyle };

    // ----- CENTERED LAYOUT -----
    if (layout === "centered") {
        return (
            <div
                className="relative min-h-[calc(100vh-120px)] flex items-center justify-center p-6"
                data-testid="login-page"
                data-layout="centered"
            >
                {/* Background */}
                <div className="absolute inset-0 bg-slate-900 overflow-hidden">
                    <div className="absolute inset-0 grid-bg opacity-20" />
                    <div
                        className="absolute inset-0 bg-cover bg-center"
                        style={{
                            backgroundImage: `url(${bgUrl})`,
                            opacity: bgOpacity,
                            filter: content.login_background_blur ? "blur(6px) saturate(1.1)" : "none",
                            transform: content.login_background_blur ? "scale(1.05)" : "none",
                        }}
                        data-testid="login-background"
                    />
                    {/* Dark vignette for readability */}
                    <div className="absolute inset-0 bg-gradient-to-br from-slate-900/60 via-slate-900/30 to-slate-900/60" />
                </div>

                {/* Hero text above form */}
                <div className="relative z-10 w-full max-w-md flex flex-col items-center text-center gap-8">
                    <div className="flex flex-col items-center gap-4">
                        <LogoBlock content={content} size={logoSize} />
                        <div>
                            <h2
                                className="font-display font-black uppercase leading-none text-4xl md:text-5xl"
                                style={{ color: content.login_hero_line1_color }}
                                data-testid="login-hero-heading"
                            >
                                {content.login_hero_line1}<br />
                                <span style={{ color: line2Color }}>{content.login_hero_line2}</span><br />
                                {content.login_hero_line3}
                            </h2>
                            <p
                                className="mt-4 max-w-sm text-sm mx-auto"
                                style={{ color: content.login_hero_subtitle_color }}
                            >
                                {content.login_hero_subtitle}
                            </p>
                        </div>
                    </div>

                    <LoginForm {...formProps} />
                </div>
            </div>
        );
    }

    // ----- SPLIT LAYOUT -----
    return (
        <div className="min-h-[calc(100vh-120px)] grid md:grid-cols-2" data-testid="login-page" data-layout="split">
            <div className="hidden md:block relative overflow-hidden bg-slate-900">
                <div className="absolute inset-0 grid-bg opacity-20" />
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage: `url(${bgUrl})`,
                        opacity: bgOpacity,
                        filter: content.login_background_blur ? "blur(6px) saturate(1.1)" : "none",
                        transform: content.login_background_blur ? "scale(1.05)" : "none",
                    }}
                    data-testid="login-background"
                />
                <div className="relative p-12 h-full flex flex-col justify-between text-white">
                    <div>
                        <LogoBlock content={content} size={logoSize} />
                        <div className="label-kicker text-slate-400 mt-6">System Access</div>
                    </div>
                    <div>
                        <h2
                            className="font-display font-black text-4xl uppercase leading-none"
                            style={{ color: content.login_hero_line1_color }}
                            data-testid="login-hero-heading"
                        >
                            {content.login_hero_line1}<br />
                            <span style={{ color: line2Color }}>{content.login_hero_line2}</span><br />
                            {content.login_hero_line3}
                        </h2>
                        <p className="mt-4 max-w-sm" style={{ color: content.login_hero_subtitle_color }}>
                            {content.login_hero_subtitle}
                        </p>
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-center p-8">
                <LoginForm {...formProps} />
            </div>
        </div>
    );
}
